import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
  BookOpen,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Filter,
  ArrowDownToLine,
  ArrowUpFromLine,
  Download,
  Calendar,
} from "lucide-react";
import { trustApi, clientsApi, PaginatedResponse } from "../../services/api";
import SearchableSelect, { SelectOption } from "../ui/SearchableSelect";

interface Props {
  bankAccountId: string;
}

interface Receipt {
  _id: string;
  type: "receipt";
  clientId: { _id: string; displayName: string } | string;
  matterId: { _id: string; title: string } | string;
  amountCents: number;
  status: string;
  receivedAt: string;
  payer: { name: string };
  reference: string | null;
  method: string;
}

interface Disbursement {
  _id: string;
  type: "disbursement";
  clientId: { _id: string; displayName: string } | string;
  matterId: { _id: string; title: string } | string;
  amountCents: number;
  status: string;
  requestedAt: string;
  payee: { name: string };
  purpose: string;
}

type LedgerEntry = (Receipt | Disbursement) & { date: string; direction: "in" | "out" };

function formatCents(cents: number): string {
  return new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(cents / 100);
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" });
}

const TrustLedgerTab: React.FC<Props> = ({ bankAccountId }) => {
  const [entries, setEntries] = useState<LedgerEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [clientFilter, setClientFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [clients, setClients] = useState<any[]>([]);

  // Load clients for filter
  useEffect(() => {
    clientsApi.list({ limit: 100 }).then((res) => setClients(res.data?.data || []));
  }, []);

  const fetchLedger = useCallback(async () => {
    if (!bankAccountId) {
      setEntries([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const params: Record<string, any> = { bankAccountId, limit: 100 };
      if (clientFilter) params.clientId = clientFilter;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;

      const [receiptsRes, disbursementsRes] = await Promise.all([
        trustApi.listReceipts(params),
        trustApi.listDisbursements({
          bankAccountId,
          limit: 100,
          ...(clientFilter ? { clientId: clientFilter } : {}),
        }),
      ]);

      const receipts = (receiptsRes.data?.data || [])
        .filter((r: any) => r.status !== "void")
        .map((r: any) => ({
          ...r,
          type: "receipt" as const,
          direction: "in" as const,
          date: r.receivedAt,
        }));

      const disbursements = (disbursementsRes.data?.data || [])
        .filter((d: any) => d.status === "released")
        .map((d: any) => ({
          ...d,
          type: "disbursement" as const,
          direction: "out" as const,
          date: d.requestedAt,
        }));

      // Merge and sort chronologically
      const merged = [...receipts, ...disbursements].sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
      );

      setEntries(merged);
    } catch (err) {
      console.error("Failed to load ledger:", err);
    } finally {
      setLoading(false);
    }
  }, [bankAccountId, clientFilter, dateFrom, dateTo]);

  useEffect(() => {
    fetchLedger();
  }, [fetchLedger]);

  // Calculate running balance
  let runningBalance = 0;
  const entriesWithBalance = entries.map((entry) => {
    if (entry.direction === "in") {
      runningBalance += entry.amountCents;
    } else {
      runningBalance -= entry.amountCents;
    }
    return { ...entry, runningBalance };
  });

  return (
    <div>
      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <SearchableSelect
              options={clients.map((c: any) => ({
                value: c._id,
                label: c.displayName,
              }))}
              value={clientFilter || null}
              onChange={(val) => setClientFilter(val || "")}
              placeholder="All Clients"
              icon={Filter}
              clearable
            />
          </div>
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-emerald-600"
              placeholder="From"
            />
          </div>
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-emerald-600"
              placeholder="To"
            />
          </div>
          <button
            disabled
            title="Export coming soon"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 opacity-50 cursor-not-allowed"
          >
            <Download className="h-4 w-4" />
            Export
          </button>
        </div>
      </div>

      {/* Ledger Table */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
          </div>
        ) : entries.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="flex flex-col items-center justify-center py-20"
          >
            <div className="relative mb-6">
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-emerald-100 to-emerald-50 blur-xl scale-150 opacity-60" />
              <div className="relative p-5 rounded-3xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 shadow-sm">
                <BookOpen className="h-10 w-10" style={{ color: "var(--color-primary)" }} />
              </div>
            </div>
            <h3 className="text-xl font-bold text-slate-800 mb-2">No ledger entries</h3>
            <p className="text-sm text-slate-500 text-center max-w-md leading-relaxed">
              Trust receipts and disbursements will appear here as a chronological ledger with running balances.
            </p>
          </motion.div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200" style={{ backgroundColor: "var(--color-primary)" }}>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider">Date</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider">Type</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider">Client</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider hidden md:table-cell">Description</th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider">In</th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider">Out</th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider">Balance</th>
                </tr>
              </thead>
              <tbody>
                {entriesWithBalance.map((entry, i) => {
                  const clientName =
                    typeof entry.clientId === "object" && entry.clientId
                      ? entry.clientId.displayName
                      : "Unknown";

                  const description =
                    entry.direction === "in"
                      ? `Deposit from ${(entry as any).payer?.name || "N/A"}`
                      : `Disbursement to ${(entry as any).payee?.name || "N/A"} - ${(entry as any).purpose || ""}`;

                  return (
                    <motion.tr
                      key={`${entry.type}-${entry._id}`}
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.01 }}
                      className="border-b border-slate-100 hover:bg-slate-50"
                    >
                      <td className="px-6 py-3 text-sm text-slate-600">{formatDate(entry.date)}</td>
                      <td className="px-6 py-3">
                        {entry.direction === "in" ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                            <ArrowDownToLine className="h-3 w-3" /> Receipt
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                            <ArrowUpFromLine className="h-3 w-3" /> Disbursement
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-3 text-sm font-medium text-slate-800">{clientName}</td>
                      <td className="px-6 py-3 text-sm text-slate-500 hidden md:table-cell truncate max-w-[250px]">{description}</td>
                      <td className="px-6 py-3 text-right text-sm font-medium text-emerald-700">
                        {entry.direction === "in" ? formatCents(entry.amountCents) : ""}
                      </td>
                      <td className="px-6 py-3 text-right text-sm font-medium text-red-600">
                        {entry.direction === "out" ? formatCents(entry.amountCents) : ""}
                      </td>
                      <td className={`px-6 py-3 text-right text-sm font-bold ${entry.runningBalance >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                        {formatCents(entry.runningBalance)}
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrustLedgerTab;
